import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit, ViewChild
} from '@angular/core';
import { Company, Invitation, Profile, UserApp } from 'app/models';
import {
  ConfirmDeleteDialogComponent,
  InviteUserDialogComponent,
  ResendInviteDialogComponent
} from '../../dialogs';
import { InvitationsService, SnackBarService, UsersService } from 'app/services';

import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Subject } from 'rxjs';
import { SnackbarType } from 'app/constants/Enums';
import { TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import * as tableHelpers from 'app/helpers/tableSelectionHelpers';

@Component({
  standalone: false,
  selector: 'app-company-invites',
  templateUrl: './company-invites.component.html',
  styleUrls: ['./company-invites.component.scss']
})
export class CompanyInvitesComponent implements OnInit, OnDestroy {
  tableColumns = [
    { property: 'status', title: 'Status' },
    { property: 'email', title: 'Email' }
  ];
  @Input() usersProfile: Subject<Profile[]>;
  @Input() companyBranches: Subject<Company[]>;
  @Input() branches: Company[];
  @Input() companyId: string;
  @Input() inviteList: Subject<Invitation[]>;

  public invitations: Invitation[];
  invitationsError: string;
  public branchAssignments = new FormControl();
  public email: FormControl;
  public isAdmin: boolean;
  public emailExists: ValidatorFn;
  public apps: Array<UserApp> = [];
  users: Profile[] = [];
  private onDestroy$ = new Subject<void>();
  usersError: string;
  selectedInvitation: any;
  directToBranch = false;
  currentPageInvites: Invitation[];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  invitesDataSource = new MatTableDataSource([]);
  selection = new SelectionModel<Profile>(true, []);
  displayedColumns = [
    'select',
    'email',
    'status'
  ];
  allSubscriptionsCompleted = false;
  constructor(
    private _usersService: UsersService,
    public _invitationsService: InvitationsService,
    private _translateService: TranslateService,
    private _dialog: MatDialog,
    private chgDet: ChangeDetectorRef,
    private _snackbarService: SnackBarService
  ) { }

  ngOnInit() {
    this.companyBranches
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(branches => {
        this.branches = branches;
      });

    if (this.companyId) {
      this._usersService.loading = true;
    }
    this.usersProfile.pipe(takeUntil(this.onDestroy$)).subscribe(users => {
      this.users = users;
    });
    this.inviteList.pipe(takeUntil(this.onDestroy$)).subscribe(invites => {
      this.invitations = invites;
      this.invitesDataSource.data = this.invitations;
      this.invitesDataSource.paginator = this.paginator;
      this.invitesDataSource
        .connect()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((invitesList) => {
          this.currentPageInvites = invitesList;
        });
    });

    // Set flag to true once all three subscriptions have emitted
    combineLatest([
      this.companyBranches,
      this.usersProfile,
      this.inviteList
    ])
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.allSubscriptionsCompleted = true;
        this._usersService.loading = false;
      });
  }
  ngOnDestroy() {
    this.onDestroy$.next(null);
  }
  selectedRecords(event) {
    this.selectedInvitation = event;
  }

  isAllSelected = () => tableHelpers.isAllSelected(this.currentPageInvites, this.selection.selected);

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle = () => tableHelpers.masterToggle(this.currentPageInvites, this.selection);

  isPartiallySelected = () => tableHelpers.isPartiallySelected(this.currentPageInvites, this.selection.selected);

  confirmDelete(): void {
    // translate strings
    let title;
    this._translateService
      .get('COMPANY_DELETE_USERS.delete_users_invite')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (title = value));
    let removeInvitation;
    this._translateService
      .get('SNACKBAR.remove_invitation')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (removeInvitation = value));
    let noSelected;
    this._translateService
      .get('SNACKBAR.no_invite_selected')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (noSelected = value));

    if (this.selection.selected.length === 0) {
      this._snackbarService.showSnackbar(
        SnackbarType.info,
        removeInvitation,
        noSelected
      );
      return;
    }
    const dialog = this._dialog.open(ConfirmDeleteDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: {
        users: this.selection.selected,
        title,
        statusColumn: true
      }
    });

    dialog.afterClosed().subscribe(result => {
      if (result) {
        const invitationEmails = this.selection.selected.map(
          user => user.email
        );
        this._invitationsService.deleteInvitations(invitationEmails);
        this.selection.clear();
      }
    });
  }

  resendconfirmation(): void {
    // translate strings
    let title;
    this._translateService
      .get('COMPANY_DELETE_USERS.resend_users_confirmation')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (title = value));

    let resendInvite;
    this._translateService
      .get('SNACKBAR.resent_invitations')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (resendInvite = value));
      
    let noUserInvite;
    this._translateService
      .get('SNACKBAR.no_user_to_invite')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (noUserInvite = value));

    if (this.selection.selected.length === 0) {
      this._snackbarService.showSnackbar(
        SnackbarType.info,
        resendInvite,
        noUserInvite
      );
      return;
    }
    const dialog = this._dialog.open(ResendInviteDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: {
        users: this.selection.selected,
        title
      }
    });

    dialog.afterClosed().subscribe(result => {
      if (result) {
        this.resendInvites();
      }

    });
  }

  resendInvites() {
    if (this.selection.selected) {
      const emails: string[] = this.selection.selected.map(
        invite => invite.email
      );
      this.resendInvite(emails);
    }
    this.selection.clear();
  }

  resendInvite(invitations: string[]): void {
    this._invitationsService.resendInvitation(invitations);
  }

  setFormControl() {
    this.emailExists = (input: AbstractControl): ValidationErrors | null => {
      const email = input.value?.toLowerCase();
      const userExists = this.users.some(user => user['email'] === email);
      const invitationExists = this.invitations.some(invite => invite['email'] === email);

      if (userExists) {
        return { emailExistsInUsers: true };
      } else if (invitationExists) {
        return { emailExistsInInvitations: true };
      }
      return null;
    };
    this.email = new FormControl('', [Validators.email, this.emailExists]);
  }

  inviteUsers(): void {
    if (this.users && this.invitations) {
      this.apps = [];
      this.setFormControl();
      let title;
      let message;

      this._translateService
        .get('COMPANY_ADD_USERS.invite_users_title')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (title = value));
      this._translateService
        .get('COMPANY_ADD_USERS.invite_users_message')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(value => (message = value));

      const toggleAdmin = value => {
        this.isAdmin = value;
      };

      const toggleDirectToBranch = value => {
        this.directToBranch = value;
      };

      const setApps = value => {
        this.apps = value;
      }

      this.clearForms();
      const dialog = this._dialog.open(InviteUserDialogComponent, {
        disableClose: true,
        width: '800px',
        height: '630px',
        autoFocus: true,
        data: {
          title,
          message,
          branches: this.branches,
          users: this.users,
          invitations: this.invitations,
          branchAssignments: this.branchAssignments,
          email: this.email,
          emailExists: this.emailExists,
          apps: this.apps,
          toggleDirectToBranch,
          toggleAdmin,
          setApps
        }
      });

      dialog.afterClosed().subscribe(result => {
        if (result) {
          this._usersService.loading = true;
          if (this.email.value) {
            this._invitationsService
              .sendInvitation(
                this.email.value,
                this.branchAssignments.value || [],
                this.apps || [],
                this.isAdmin,
                this.directToBranch
              )
              .subscribe(res => {
                if (res) {
                  this._invitationsService.getCompanyInvitations(this.companyId);
                } else {
                  this._usersService.loading = false;
                }
              });
          }
        } else {
          this.directToBranch = false;
        }
      });
    }
  }

  clearForms(): void {
    this.branchAssignments = new FormControl();
    this.email.reset();
    this.directToBranch = false;
    this.isAdmin = false;
  }
  isBranchAdmin(invite){
    return invite.branchAssignments && invite.branchAssignments?.some(branch => branch.isBranchAdmin)
  }
}
